import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MobileNavigation from '../../components/ui/MobileNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import components
import EmployeeTable from './components/EmployeeTable';
import EmployeeFilters from './components/EmployeeFilters';
import AddEmployeeModal from './components/AddEmployeeModal';
import EmployeeProfileModal from './components/EmployeeProfileModal';
import BulkActionsBar from './components/BulkActionsBar';
import EmployeeStats from './components/EmployeeStats';
import { get } from 'react-hook-form';
import { useSelector } from 'react-redux';
import UseEmployeeHook from 'hooks/UseEmployeeHook';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Employee management states
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const {employee} = useSelector((state) => state.employee);
  // console.log(employee);
  const {getAllEmployee, updateEmployee} = UseEmployeeHook()
  // Mock employee data
  const mockEmployees = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@company.com",
      phone: "+1 (555) 123-4567",
      whatsappNumber: "+1 (555) 123-4567",
      role: "manager",
      department: "sales",
      employeeId: "EMP001",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah",
      status: "active",
      billingLimit: 5000,
      billingUsed: 3200,
      lastLogin: new Date('2025-01-30T09:15:00'),
      createdAt: new Date('2024-03-15'),
      permissions: {
        canCreateBills: true,
        canViewReports: true,
        canManageProducts: true,
        canAccessFinance: true
      }
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      email: "michael.rodriguez@company.com",
      phone: "+1 (555) 234-5678",
      whatsappNumber: "+1 (555) 234-5678",
      role: "supervisor",
      department: "operations",
      employeeId: "EMP002",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Michael",
      status: "active",
      billingLimit: 3000,
      billingUsed: 1800,
      lastLogin: new Date('2025-01-30T08:30:00'),
      createdAt: new Date('2024-05-20'),
      permissions: {
        canCreateBills: true,
        canViewReports: true,
        canManageProducts: false,
        canAccessFinance: false
      }
    },
    {
      id: 3,
      name: "Emily Chen",
      email: "emily.chen@company.com",
      phone: "+1 (555) 345-6789",
      whatsappNumber: "+1 (555) 345-6789",
      role: "employee",
      department: "marketing",
      employeeId: "EMP003",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Emily",
      status: "active",
      billingLimit: 2000,
      billingUsed: 950,
      lastLogin: new Date('2025-01-29T16:45:00'),
      createdAt: new Date('2024-07-10'),
      permissions: {
        canCreateBills: true,
        canViewReports: false,
        canManageProducts: false,
        canAccessFinance: false
      }
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@company.com",
      phone: "+1 (555) 456-7890",
      whatsappNumber: "+1 (555) 456-7890",
      role: "employee",
      department: "finance",
      employeeId: "EMP004",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=David",
      status: "inactive",
      billingLimit: 1500,
      billingUsed: 0,
      lastLogin: new Date('2025-01-25T14:20:00'),
      createdAt: new Date('2024-09-05'),
      permissions: {
        canCreateBills: false,
        canViewReports: false,
        canManageProducts: false,
        canAccessFinance: true
      }
    },
    {
      id: 5,
      name: "Jessica Williams",
      email: "jessica.williams@company.com",
      phone: "+1 (555) 567-8901",
      whatsappNumber: "+1 (555) 567-8901",
      role: "intern",
      department: "hr",
      employeeId: "EMP005",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Jessica",
      status: "active",
      billingLimit: 500,
      billingUsed: 150,
      lastLogin: new Date('2025-01-30T10:00:00'),
      createdAt: new Date('2024-11-01'),
      permissions: {
        canCreateBills: false,
        canViewReports: false,
        canManageProducts: false,
        canAccessFinance: false
      }
    },
    {
      id: 6,
      name: "Robert Davis",
      email: "robert.davis@company.com",
      phone: "+1 (555) 678-9012",
      whatsappNumber: "+1 (555) 678-9012",
      role: "supervisor",
      department: "it",
      employeeId: "EMP006",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Robert",
      status: "suspended",
      billingLimit: 2500,
      billingUsed: 2500,
      lastLogin: new Date('2025-01-20T11:30:00'),
      createdAt: new Date('2024-04-12'),
      permissions: {
        canCreateBills: true,
        canViewReports: true,
        canManageProducts: true,
        canAccessFinance: false
      }
    }
  ];

 useEffect(() => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    setUser(JSON.parse(storedUser));
  } else {
    navigate('/whats-app-authentication');
    return;
  }
}, [navigate]);

// Fetch employees on mount
useEffect(() => {
  getAllEmployee();
}, []);

// Update local employees whenever Redux employee changes
useEffect(() => {
  if (employee) {
    setEmployees(employee);
    setLoading(false);
  }
}, [employee]);

  // Filter employees based on search and filters
  const filteredEmployees = useMemo(() => {
    return employees?.filter(employee => {
      const matchesSearch = !searchTerm || 
        employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase());




      
      const matchesRole = !roleFilter || employee.role === roleFilter;
      const matchesStatus = !statusFilter || employee.status === statusFilter;
      const matchesDepartment = !departmentFilter || employee.department === departmentFilter;
      
      return matchesSearch && matchesRole && matchesStatus && matchesDepartment;
    });
  }, [employees, searchTerm, roleFilter, statusFilter, departmentFilter]);

  // Handle employee selection
  const handleSelectEmployee = (employeeId, checked) => {
    if (checked) {
      setSelectedEmployees(prev => [...prev, employeeId]);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== employeeId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmployees(filteredEmployees.map(emp => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  // Handle employee actions
  const handleAddEmployee = (newEmployee) => {
    setEmployees(prev => [...prev, newEmployee]);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowAddModal(true);
  };

  const handleViewProfile = (employee) => {
    setSelectedEmployee(employee);
    setShowProfileModal(true);
  };

  const handleResetPassword = (employee) => {
    // Mock password reset
    alert(`Password reset link sent to ${employee.whatsappNumber} via WhatsApp`);
  };

  const handleDeactivateEmployee = async(b, a) => {


    // console.log(b)
    
    if(a){
      let val = {...b, active: true}
await updateEmployee(val)
    }else{
       let val = {...b, active: false}
      // console.log(val)
await updateEmployee(val)
    }
return
    // if (confirm(`Are you sure you want to deactivate ${employee.name}?`)) {
    //   setEmployees(prev => 
    //     prev.map(emp => 
    //       emp._id === employee._id 
    //         ? { ...emp, status: 'inactive' }
    //         : emp
    //     )
    //   );
    // }
  };

  // Handle bulk actions
  const handleBulkStatusUpdate = (status) => {
    setEmployees(prev => 
      prev.map(emp => 
        selectedEmployees.includes(emp.id)
          ? { ...emp, status }
          : emp
      )
    );
    setSelectedEmployees([]);
  };

  const handleBulkPermissionChange = (permission) => {
    // Mock bulk permission change
    alert(`Bulk permission change for ${selectedEmployees.length} employees`);
    setSelectedEmployees([]);
  };

  const handleBulkDelete = () => {
    if (confirm(`Are you sure you want to delete ${selectedEmployees.length} employees?`)) {
      setEmployees(prev => 
        prev.filter(emp => !selectedEmployees.includes(emp.id))
      );
      setSelectedEmployees([]);
    }
  };

  // Handle filter clearing
  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setDepartmentFilter('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          user={user} 
          onSidebarToggle={() => setMobileNavOpen(true)}
          sidebarCollapsed={sidebarCollapsed}
        />
        <Sidebar 
          user={user}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <MobileNavigation 
          user={user}
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />
        
        <main className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-60'
        }`}>
          <div className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading employees...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user} 
        onSidebarToggle={() => setMobileNavOpen(true)}
        sidebarCollapsed={sidebarCollapsed}
      />
      <Sidebar 
        user={user}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <MobileNavigation 
        user={user}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      <main className={`pt-16 transition-all duration-200 ${
        sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-60'
      }`}>
        <div className="p-6">
          <Breadcrumb user={user} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Employee Management</h1>
              <p className="text-muted-foreground mt-2">
                Manage employee accounts, and billing 
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                iconSize={16}
              >
                Import CSV
              </Button> */}
              <Button
                variant="default"
                onClick={() => setShowAddModal(true)}
                iconName="UserPlus"
                iconPosition="left"
                iconSize={16}
              >
                Add Employee
              </Button>
            </div>
          </div>

          {/* Employee Statistics */}
          <EmployeeStats employees={employees} />

          {/* Filters */}
          <EmployeeFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            departmentFilter={departmentFilter}
            onDepartmentFilterChange={setDepartmentFilter}
            onClearFilters={handleClearFilters}
          />

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <p className="text-sm text-muted-foreground">
                Showing {filteredEmployees?.length} of {employees?.length} employees
              </p>
              {selectedEmployees.length > 0 && (
                <p className="text-sm text-primary font-medium">
                  {selectedEmployees?.length} selected
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                iconSize={14}
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Filter"
                iconPosition="left"
                iconSize={14}
              >
                More Filters
              </Button>
            </div>
          </div>

          {/* Employee Table */}
          <EmployeeTable
            employees={filteredEmployees}
            onEdit={handleEditEmployee}
            onViewProfile={handleViewProfile}
            onResetPassword={handleResetPassword}
            onDeactivate={handleDeactivateEmployee}
            selectedEmployees={selectedEmployees}
            onSelectEmployee={handleSelectEmployee}
            onSelectAll={handleSelectAll}
          />

          {/* Empty State */}
          {filteredEmployees?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No employees found</h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm || roleFilter || statusFilter || departmentFilter
                  ? 'Try adjusting your filters to see more results.' :'Get started by adding your first employee.'}
              </p>
              {!searchTerm && !roleFilter && !statusFilter && !departmentFilter && (
                <Button
                  variant="default"
                  onClick={() => setShowAddModal(true)}
                  iconName="UserPlus"
                  iconPosition="left"
                  iconSize={16}
                >
                  Add First Employee
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      <AddEmployeeModal
        isOpen={showAddModal}
        employee={selectedEmployee}
        onClose={() => {
          setShowAddModal(false);
          setSelectedEmployee(null);
        }}
        onSubmit={handleAddEmployee}
      />

      <EmployeeProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedEmployees.length}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onBulkPermissionChange={handleBulkPermissionChange}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => setSelectedEmployees([])}
      />
    </div>
  );
};

export default EmployeeManagement;